import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSession } from '../../state/session.tsx';
import { quoteApi } from '../../api/endpoints.ts';
import type { QuoteEditorApi } from '../../hooks/use-quote-editor.ts';
import { readQuoteWalkthrough } from '@shared/walkthrough-gate.ts';
import { Button, Checkbox, Alert } from '../ui.tsx';
import { NumberInput } from '../number-input.tsx';

export function WalkthroughReviewCard({ editor, readOnly }: { editor: QuoteEditorApi; readOnly: boolean }) {
  const { t } = useTranslation();
  const { can } = useSession();
  const [visited, setVisited] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const q = editor.quote;
  if (!q) return null;
  const state = q.walkthrough ?? readQuoteWalkthrough(null);
  const disabled = readOnly || busy;
  function patch(values: Partial<typeof state.assessment>) {
    setVisited(false);
    editor.update((draft) => ({
      ...draft,
      walkthrough: {
        ...(draft.walkthrough ?? state),
        assessment: { ...(draft.walkthrough ?? state).assessment, ...values },
      },
    }));
  }
  async function completeReview() {
    if (!visited || busy || readOnly) return;
    const resume = editor.suspendAutosave();
    setBusy(true);
    setError(null);
    try {
      const saved = await editor.save();
      if (!saved) throw new Error('unsaved');
      const savedThrough = editor.savedEditCount();
      const result = await quoteApi.reviewWalkthrough(saved.id, saved.version, saved.walkthroughStatus!.assessmentKey);
      editor.applyServerQuote(result, savedThrough);
      setVisited(false);
    } catch {
      setError(t('walkthrough.saveError'));
    } finally {
      resume();
      setBusy(false);
    }
  }
  const params = new URLSearchParams({ customerId: q.customerId, frequency: 'one_time' });
  if (q.propertyId) params.set('propertyId', q.propertyId);
  return (
    <section className="card mb-6" aria-labelledby="walkthrough-heading">
      <h2 id="walkthrough-heading" className="mb-3 text-lg font-semibold">
        {t('walkthrough.heading')}
      </h2>
      <p className="mb-3">{t('walkthrough.hint')}</p>
      <Checkbox
        label={t('walkthrough.manual')}
        checked={state.assessment.manualRequired}
        disabled={disabled}
        onChange={(e) => patch({ manualRequired: e.target.checked })}
      />
      <NumberInput
        label={t('walkthrough.pets')}
        value={state.assessment.petCount}
        min={0}
        max={100}
        disabled={disabled}
        onValueChange={(petCount) => patch({ petCount })}
      />
      {q.walkthroughStatus?.pricingBlocked && (
        <Alert tone="warning">
          {t(q.walkthroughStatus.policyChanged ? 'walkthrough.policyChanged' : 'walkthrough.required')}
        </Alert>
      )}
      {q.walkthroughStatus?.reviewed && <p>{t('walkthrough.reviewed')}</p>}
      {can('quotes:price_override') && !readOnly && (
        <>
          <Checkbox
            label={t('walkthrough.visited')}
            checked={visited}
            disabled={busy}
            onChange={(e) => setVisited(e.target.checked)}
          />
          <Button disabled={busy || !visited || q.walkthroughStatus?.reviewed} onClick={completeReview}>
            {t('walkthrough.review')}
          </Button>
        </>
      )}
      {can('schedule:write') && (
        <p className="mt-3">
          <Link className="font-semibold text-[color:var(--color-brand)] underline" to={`/schedule/new?${params}`}>
            {t('walkthrough.book')}
          </Link>
          <span className="block text-sm">{t('walkthrough.bookingHint')}</span>
        </p>
      )}
      {error && <Alert tone="error">{error}</Alert>}
    </section>
  );
}
